import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface HierarchyNode {
  uuid: string;
  name: string;
  email: string;
  roleId: number;
  roleLabel: string;
  managerUuid: string | null;
  directReports: number;
  children: HierarchyNode[];
}

export interface TreeResponse {
  tree: HierarchyNode[];
  totalUsers: number;
  rootCount: number;
}

export interface SearchHit {
  uuid: string;
  name: string;
  email: string;
  roleLabel: string;
}

export interface NodeDetail {
  user: HierarchyNode;
  manager: { uuid: string; name: string; email: string; roleLabel: string } | null;
  directReports: { uuid: string; name: string; email: string; roleLabel: string }[];
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

type Result<T> = { success: boolean; data?: T; error?: { message: string } | null; message?: string };

const BASE = '/super-admin/hierarchy';

const ok = <T,>(data: T, message?: string): Result<T> => ({ success: true, data, error: null, message });
const fail = <T,>(e: any): Result<T> => ({ success: false, error: { message: e?.message ?? 'Request failed' } });

export const getTree = async (params: { rootUuid?: string; depth?: number } = {}): Promise<Result<TreeResponse>> => {
  try {
    const q = new URLSearchParams();
    if (params.rootUuid) q.append('rootUuid', params.rootUuid);
    if (params.depth) q.append('depth', String(params.depth));
    const res = await httpClient.get<ApiResponse<TreeResponse>>(`${BASE}/tree?${q.toString()}`);
    return ok(res.data);
  } catch (e) { return fail(e); }
};

export const searchUsers = async (query: string): Promise<Result<SearchHit[]>> => {
  try {
    const res = await httpClient.get<ApiResponse<SearchHit[]>>(`${BASE}/search?q=${encodeURIComponent(query)}`);
    return ok(res.data || []);
  } catch (e) { return fail(e); }
};

export const getUserNode = async (uuid: string): Promise<Result<NodeDetail>> => {
  try {
    const res = await httpClient.get<ApiResponse<NodeDetail>>(`${BASE}/user/${uuid}`);
    return ok(res.data);
  } catch (e) { return fail(e); }
};

export const reassignManager = async (uuid: string, managerUuid: string | null): Promise<Result<null>> => {
  try {
    const res = await httpClient.patch<ApiResponse<null>>(`${BASE}/user/${uuid}/manager`, { managerUuid });
    return ok(null, res.message);
  } catch (e) { return fail(e); }
};
